import type { LabelOperator, NodeDetails, Relationship } from '@/types';

/**
 * FlatGraph: A flat representation of GraphQuery for efficient editing.
 * Uses stable UUIDs for identity, enabling O(1) lookups and updates.
 *
 * Architecture: GraphQuery uses nested NodeDetails with relationships.target/source.
 * FlatGraph flattens this tree into Maps for direct access.
 */
export interface FlatGraph {
  /** Map of node ID to node data */
  nodes: Map<string, FlatGraphNode>;

  /** Map of edge ID to edge data */
  edges: Map<string, FlatGraphEdge>;

  /** ID of the root node (entry point of query, from GraphQuery.node) */
  rootId: string;
}

/**
 * FlatGraphNode: A single node in the flat graph.
 * Represents a NodeDetails from GraphQuery with stable UUID.
 */
export interface FlatGraphNode {
  /** Stable UUID for identity during editing */
  id: string;

  /** Original NodeDetails data (labels, filters, etc.) */
  data: {
    labels?: NodeDetails['labels'];
    label_operator?: LabelOperator;
    sorted?: NodeDetails['sorted'];
    filters?: NodeDetails['filters'];
    self_edges?: NodeDetails['self_edges'];
    alias?: NodeDetails['alias'];
  };

  /** Optional UI state (positions, validation, etc.) */
  uiState?: {
    position?: { x: number; y: number };
    isCollapsed?: boolean;
    validationErrors?: string[];
  };
}

/**
 * FlatGraphEdge: A single edge in the flat graph.
 * Represents a relationship from NodeDetails.relationships[] with source/target IDs.
 *
 * Direction field indicates whether edge came from relationship.target (outgoing)
 * or relationship.source (incoming) in the original GraphQuery structure.
 */
export interface FlatGraphEdge {
  /** Stable UUID for identity during editing */
  id: string;

  /** Relationship data (label, length, optional, etc.) */
  data: {
    label: Relationship | Relationship[];
    label_operator?: LabelOperator;
    length?: number;
    optional?: boolean;
    select_one?: boolean;
    not_exists?: boolean;
  };

  /** Foreign key to source node (the node this relationship belongs to) */
  sourceId: string;

  /** Foreign key to target node (NodeDetails from relationship.target) */
  targetId: string;

  /** Direction of the relationship: 'target' for outgoing, 'source' for incoming */
  direction: 'target' | 'source';
}

/**
 * Type guard to check if a relationship has a target node
 */
export function hasTarget(
  rel: NonNullable<NodeDetails['relationships']>[number]
): rel is NonNullable<NodeDetails['relationships']>[number] & {
  target: NodeDetails;
} {
  return rel.target !== undefined;
}

/**
 * Type guard to check if a relationship has a source node
 */
export function hasSource(
  rel: NonNullable<NodeDetails['relationships']>[number]
): rel is NonNullable<NodeDetails['relationships']>[number] & {
  source: NodeDetails;
} {
  return rel.source !== undefined;
}
